import { ProviderConfig, StoryBible } from "../../core/types";
import { getProviders } from "../../core/storage";
import { wireLanguageToggleButtons, updateLanguageToggleButtons } from "../../core/i18n";
import { renderOnboarding } from "./onboarding";
import { renderProviderList } from "./providerList";
import { renderProviderForm } from "./providerForm";
import { renderChat, setContentMode } from "./chat";
import { renderSessionHistory } from "./sessionHistory";
import { renderStoryBibleList, renderStoryBibleEditor } from "./storyBible";
import { renderSettings } from "./settings";

type Screen =
  | "auto"
  | "providerList"
  | "providerForm"
  | "sessionHistory"
  | "storyBibleList"
  | "storyBibleForm"
  | "settings";

// Replayed by the footer's language toggle (see wireLanguageToggle below) so switching languages
// re-renders whatever screen is currently showing instead of always bouncing back to "auto" —
// the footer sits outside #app-root, so it has no other way to know what's on screen right now.
let lastRenderArgs: {
  screen: Screen;
  editingProvider: ProviderConfig | null;
  editingStoryBible: StoryBible | null;
} | null = null;

// onLanguageSwitch (optional) runs alongside the app's own re-render, in the same click
// listener — for standalone.ts's own chrome (the storage-isolation notice) that lives outside
// #app-root and so isn't covered by renderApp at all. A second, separate
// wireLanguageToggleButtons call on the same document.body scope was tried for this instead
// (standalone.ts calling it a second time after mountApp) — that attached two click listeners to
// the same footer buttons, each independently calling setLanguage/updateLanguageToggleButtons on
// every click. Functionally harmless (idempotent) but genuinely double work per click; folded
// into one listener instead.
function wireLanguageToggle(root: HTMLElement, onLanguageSwitch?: () => void): void {
  wireLanguageToggleButtons(document.body, () => {
    const args = lastRenderArgs ?? {
      screen: "auto" as const,
      editingProvider: null,
      editingStoryBible: null,
    };
    renderApp(root, args.screen, args.editingProvider, args.editingStoryBible);
    onLanguageSwitch?.();
  });
  updateLanguageToggleButtons();
}

// Shared by both entry points (taskpane.ts, gated behind Office.onReady; standalone.ts, gated
// behind nothing but DOM-ready) — everything below this point has zero Word/Office.js dependency,
// so it works identically in a plain browser tab. See CLAUDE.md's "Standalone (non-Word)
// NovelChat" section for why this split exists. onLanguageSwitch — see wireLanguageToggle above.
export function mountApp(root: HTMLElement, onLanguageSwitch?: () => void): void {
  root.style.display = "block";
  wireLanguageToggle(root, onLanguageSwitch);
  renderApp(root, "auto", null, null);
}

function renderApp(
  root: HTMLElement,
  screen: Screen,
  editingProvider: ProviderConfig | null,
  editingStoryBible: StoryBible | null
): void {
  lastRenderArgs = { screen, editingProvider, editingStoryBible };
  const providers = getProviders();

  if (screen === "auto" && providers.length === 0) {
    renderOnboarding(root, {
      onAddProvider: () => renderApp(root, "providerForm", null, null),
      onChanged: () => renderApp(root, "auto", null, null),
    });
    return;
  }

  if (screen === "providerForm") {
    renderProviderForm(root, editingProvider, {
      // Adding a brand-new provider (editingProvider === null here) lands on the provider list
      // instead of straight back to chat, 2026-08-11, per explicit request — the natural next
      // step after adding one is "now start using it", and providerList.ts's "Start new chat"
      // button is one click from there; landing in chat gave no obvious way to act on the
      // provider you just added (especially once other chats already exist — "auto" would just
      // reopen whichever chat was last active, provider you just made nowhere in sight). Editing
      // an *existing* provider's settings keeps the old behavior — that's an adjustment to
      // something already in use, not "I just made this, now let me use it".
      onSaved: () => renderApp(root, editingProvider ? "auto" : "providerList", null, null),
      onCancel: () => renderApp(root, providers.length === 0 ? "auto" : "providerList", null, null),
    });
    return;
  }

  if (screen === "providerList") {
    renderProviderList(root, {
      onAdd: () => renderApp(root, "providerForm", null, null),
      onEdit: (provider) => renderApp(root, "providerForm", provider, null),
      // Pre-selects this provider in "Start a new chat" (ContentMode's own comment in chat.ts)
      // — set here, before the chat screen's own render, since renderChat only auto-resolves
      // contentMode when it's still null.
      onStartChat: (provider) => {
        setContentMode({ kind: "new-session", providerId: provider.id });
        renderApp(root, "auto", null, null);
      },
      onBackToChat: providers.length > 0 ? () => renderApp(root, "auto", null, null) : null,
      onChanged: () => renderApp(root, "providerList", null, null),
    });
    return;
  }

  if (screen === "sessionHistory") {
    renderSessionHistory(root, {
      onBackToChat: () => renderApp(root, "auto", null, null),
    });
    return;
  }

  // Story Bible list/editor — a real top-level screen pair mirroring providerList/providerForm
  // exactly, per explicit feedback that "Story Bible" should navigate the same way Providers/
  // History do rather than staying nested inside the chat screen. Binding a bible to a chat is
  // not handled here at all (only via Edit session's own field/pencil) — this pair is pure CRUD,
  // same as Provider list/form never touch which chat uses which provider either.
  if (screen === "storyBibleForm") {
    renderStoryBibleEditor(
      root,
      editingStoryBible ?? undefined,
      () => renderApp(root, "storyBibleList", null, null),
      () => renderApp(root, "storyBibleList", null, null)
    );
    return;
  }

  if (screen === "storyBibleList") {
    renderStoryBibleList(root, {
      onAdd: () => renderApp(root, "storyBibleForm", null, null),
      onEdit: (bible) => renderApp(root, "storyBibleForm", null, bible),
      onBackToChat: () => renderApp(root, "auto", null, null),
      onChanged: () => renderApp(root, "storyBibleList", null, null),
    });
    return;
  }

  if (screen === "settings") {
    renderSettings(root, {
      onBackToChat: () => renderApp(root, "auto", null, null),
    });
    return;
  }

  renderChat(root, {
    onManageProviders: () => renderApp(root, "providerList", null, null),
    onOpenHistory: () => renderApp(root, "sessionHistory", null, null),
    onManageStoryBibles: () => renderApp(root, "storyBibleList", null, null),
    onOpenSettings: () => renderApp(root, "settings", null, null),
  });
}
